import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { SupersedeAttendancePreview } from "./reversal";

export interface AttendanceRow {
  id: string;
  userId: string;
  userFullName: string | null;
  userEmail: string | null;
  userRole: string | null;
  userEmpId: string | null;
  calendarDate: string;
  markedInAt: string | null;
  markedInLat: string | null;
  markedInLng: string | null;
  markedInWithinGeofence: boolean;
  markedInDistanceM: number | null;
  markedInAccuracyM: number | null;
  markedInGeofenceId: string | null;
  markedInGeofenceName: string | null;
  markedInOverrideNote: string;
  markedOutAt: string | null;
  markedOutLat: string | null;
  markedOutLng: string | null;
  markedOutWithinGeofence: boolean | null;
  markedOutDistanceM: number | null;
  markedOutAccuracyM: number | null;
  markedOutGeofenceId: string | null;
  markedOutGeofenceName: string | null;
  markedOutOverrideNote: string;
  isHalfDay: boolean;
  markInRegularised: boolean;
  markOutRegularised: boolean;
  superseded: boolean;
  supersededAt: string | null;
  supersededByName: string | null;
  supersededSource: string;
  supersededReason: string;
}

export interface LeaveAdjustment {
  id: string;
  action: "shrunk" | "rejected";
  priorWindow: { start: string | null; end: string | null };
  newWindow: { start: string | null; end: string | null } | null;
}

/** Mark-out response = attendance row + any auto-applied partial-leave changes. */
export interface MarkOutResult extends AttendanceRow {
  leaveAdjustments?: LeaveAdjustment[];
}

const todayAttendanceKey = ["attendance", "today"] as const;
const meStateKey = ["me", "session-state"] as const;
const listKey = (params: object) => ["attendance", "list", params] as const;

export interface AttendanceListResponse {
  results: AttendanceRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export function useAttendanceList(params: {
  userId?: string;
  from?: string;
  to?: string;
  mismatchOnly?: boolean;
  /** Audit view only — superseded rows are hidden everywhere else. */
  includeSuperseded?: boolean;
} = {}, enabled = true) {
  return useQuery<AttendanceListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<AttendanceListResponse>("/attendance", {
          params: {
            userId: params.userId || undefined,
            from: params.from || undefined,
            to: params.to || undefined,
            mismatchOnly: params.mismatchOnly ? "true" : undefined,
            includeSuperseded: params.includeSuperseded ? "true" : undefined,
          },
        })
        .then((r) => r.data),
    enabled,
  });
}

/* --------------------------- calendar context --------------------------- */

export interface CalendarContextHoliday {
  date: string; // ISO date
  name: string;
}

export interface CalendarContextLeave {
  startsOn: string;
  endsOn: string;
  durationKind: "full_day" | "half_day" | "partial";
  leaveTypeLabel: string;
}

export interface CalendarContext {
  holidays: CalendarContextHoliday[];
  leaves: CalendarContextLeave[];
}

/** Holidays (user's sets + national) and approved leaves for a date range —
 *  lets the attendance calendar label no-punch days as Holiday / Leave
 *  instead of a false Absent. Self-permitted; other users need read_all. */
export function useCalendarContext(
  params: { userId?: string; from: string; to: string },
  enabled = true,
) {
  return useQuery<CalendarContext>({
    queryKey: ["attendance", "calendarContext", params],
    queryFn: () =>
      api
        .get<CalendarContext>("/attendance/calendar-context", {
          params: {
            userId: params.userId || undefined,
            from: params.from,
            to: params.to,
          },
        })
        .then((r) => r.data),
    enabled: enabled && !!params.from && !!params.to,
  });
}

/* ------------------------------- reversal (SA/TA) ------------------------------ */

/** Dry run — what superseding this attendance row would do. No writes. */
export function useSupersedeAttendancePreview(id: string | null, enabled = true) {
  return useQuery<SupersedeAttendancePreview>({
    queryKey: ["attendance", "supersedePreview", id],
    queryFn: () =>
      api
        .get<SupersedeAttendancePreview>(`/attendance/${id}/supersede`)
        .then((r) => r.data),
    enabled: enabled && !!id,
  });
}

export function useSupersedeAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api
        .post<AttendanceRow>(`/attendance/${id}/supersede`, { reason })
        .then((r) => r.data),
    onSuccess: () => {
      // Freeing a date unblocks leave filing for it — refresh both.
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}

export interface AttendanceTodayState {
  attendance: AttendanceRow | null;
  onLeaveToday: boolean;
  hasAssignedLocations: boolean;
}

export function useTodayAttendance(enabled = true) {
  return useQuery<AttendanceTodayState>({
    queryKey: todayAttendanceKey,
    queryFn: () =>
      api.get<AttendanceTodayState>("/attendance/today").then((r) => r.data),
    enabled,
  });
}

export function useMarkIn() {
  const qc = useQueryClient();
  return useMutation<AttendanceRow, unknown, { lat: number; lng: number; accuracyM?: number; overrideNote?: string; locationId?: string }>({
    mutationFn: (input) => api.post<AttendanceRow>("/attendance/mark-in", input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meStateKey });
      qc.invalidateQueries({ queryKey: todayAttendanceKey });
    },
  });
}

export function useMarkOut() {
  const qc = useQueryClient();
  return useMutation<MarkOutResult, unknown, { lat: number; lng: number; accuracyM?: number; overrideNote?: string; locationId?: string; confirmLeaveTrim?: boolean }>({
    mutationFn: (input) => api.post<MarkOutResult>("/attendance/mark-out", input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meStateKey });
      qc.invalidateQueries({ queryKey: todayAttendanceKey });
    },
  });
}
